/**
 * Typed wrappers over the 30-tool MCP/REST surface — one `fetch<Resource>` function per
 * tool (conventions.md §4.1 — "REST client functions: `fetch<Resource>` returning typed
 * shapes"). Each is a thin call through `callTool`, which handles the loopback token, the
 * snake↔camel mapping, the embedding guard, and the envelope unwrap.
 *
 * Every function returns `ToolResult<T>` = `{ data, meta }` — the caller (a query hook)
 * needs `meta.nextCursor` / `meta.truncated` / `meta.warnings` for pagination and
 * degradation surfacing (frontend.md §1.5, §4.2).
 *
 * The tool list is the authoritative one from `backend/aletheia/api/core/catalogue.py`.
 */

import { callTool, type ToolResult } from './client';
import type {
  ActionItemList,
  AttributionReport,
  ChunkList,
  ContradictionList,
  EntityList,
  EntityWithMetadata,
  LinkedNoteList,
  NotebookList,
  NoteTitleItem,
  NoteView,
  ResultList,
  SaveResult,
  Subgraph,
  TagList,
  TimeSeries,
  WriteResult,
} from '@/types';
import type {
  AppendToNoteRequest,
  CreateNoteRequest,
  DetectContradictionsRequest,
  FindPathRequest,
  GetActionItemsRequest,
  GetAttributionAuditRequest,
  GetBacklinksRequest,
  GetEntityCooccurrencesRequest,
  GetEntityRequest,
  GetEntityTimelineRequest,
  GetLinkedNotesRequest,
  GetNeighborhoodRequest,
  GetNoteChunksRequest,
  GetNoteRequest,
  GetNotesBatchRequest,
  GetNotesByEntityRequest,
  GetNotesByTagRequest,
  GetNotesInRangeRequest,
  GetRelatedTagsRequest,
  GetTagTimelineRequest,
  ListEntitiesRequest,
  ListNotebooksRequest,
  ListTagsRequest,
  NoteTitlesRequest,
  SearchByMetadataRequest,
  SearchChunksRequest,
  SearchHybridRequest,
  SearchLexicalRequest,
  SearchSemanticRequest,
  UpdateNoteRequest,
} from '@/types';
import type { PathResult } from '@/types';

// --- Retrieval (4) ----------------------------------------------------------

export const searchHybrid = (req: SearchHybridRequest): Promise<ToolResult<ResultList>> =>
  callTool('search_hybrid', req);

export const searchLexical = (req: SearchLexicalRequest): Promise<ToolResult<ResultList>> =>
  callTool('search_lexical', req);

export const searchSemantic = (req: SearchSemanticRequest): Promise<ToolResult<ResultList>> =>
  callTool('search_semantic', req);

export const searchChunks = (req: SearchChunksRequest): Promise<ToolResult<ChunkList>> =>
  callTool('search_chunks', req);

// --- Note access (3) --------------------------------------------------------

export const getNote = (req: GetNoteRequest): Promise<ToolResult<NoteView>> =>
  callTool('get_note', req);

export const getNoteChunks = (req: GetNoteChunksRequest): Promise<ToolResult<ChunkList>> =>
  callTool('get_note_chunks', req);

export const getNotesBatch = (
  req: GetNotesBatchRequest,
): Promise<ToolResult<{ items: NoteView[] }>> => callTool('get_notes_batch', req);

// --- Graph traversal (4) ----------------------------------------------------

export const getLinkedNotes = (
  req: GetLinkedNotesRequest,
): Promise<ToolResult<LinkedNoteList>> => callTool('get_linked_notes', req);

export const getBacklinks = (req: GetBacklinksRequest): Promise<ToolResult<LinkedNoteList>> =>
  callTool('get_backlinks', req);

/** The ONLY graph data source — neighborhood-only, never a full-corpus render (frontend.md §1.4). */
export const getNeighborhood = (
  req: GetNeighborhoodRequest,
): Promise<ToolResult<Subgraph>> => callTool('get_neighborhood', req);

/** Tier-2 deferred — explicit-trigger only, never on a latency-critical path (frontend.md §1.4, S8). */
export const findPath = (req: FindPathRequest): Promise<ToolResult<PathResult>> =>
  callTool('find_path', req);

// --- Tags (4) ---------------------------------------------------------------

export const listTags = (req: ListTagsRequest): Promise<ToolResult<TagList>> =>
  callTool('list_tags', req);

export const getNotesByTag = (req: GetNotesByTagRequest): Promise<ToolResult<ResultList>> =>
  callTool('get_notes_by_tag', req);

export const getRelatedTags = (req: GetRelatedTagsRequest): Promise<ToolResult<TagList>> =>
  callTool('get_related_tags', req);

export const getTagTimeline = (req: GetTagTimelineRequest): Promise<ToolResult<TimeSeries>> =>
  callTool('get_tag_timeline', req);

// --- Entities (5) -----------------------------------------------------------

export const listEntities = (req: ListEntitiesRequest): Promise<ToolResult<EntityList>> =>
  callTool('list_entities', req);

export const getEntity = (req: GetEntityRequest): Promise<ToolResult<EntityWithMetadata>> =>
  callTool('get_entity', req);

export const getNotesByEntity = (
  req: GetNotesByEntityRequest,
): Promise<ToolResult<ResultList>> => callTool('get_notes_by_entity', req);

export const getEntityCooccurrences = (
  req: GetEntityCooccurrencesRequest,
): Promise<ToolResult<EntityList>> => callTool('get_entity_cooccurrences', req);

export const getEntityTimeline = (
  req: GetEntityTimelineRequest,
): Promise<ToolResult<TimeSeries>> => callTool('get_entity_timeline', req);

// --- Time & structured filtering (2) ---------------------------------------

export const getNotesInRange = (
  req: GetNotesInRangeRequest,
): Promise<ToolResult<ResultList>> => callTool('get_notes_in_range', req);

export const searchByMetadata = (
  req: SearchByMetadataRequest,
): Promise<ToolResult<ResultList>> => callTool('search_by_metadata', req);

// --- Analytics (3) ----------------------------------------------------------

export const getActionItems = (
  req: GetActionItemsRequest,
): Promise<ToolResult<ActionItemList>> => callTool('get_action_items', req);

export const detectContradictions = (
  req: DetectContradictionsRequest,
): Promise<ToolResult<ContradictionList>> => callTool('detect_contradictions', req);

export const getAttributionAudit = (
  req: GetAttributionAuditRequest,
): Promise<ToolResult<AttributionReport>> => callTool('get_attribution_audit', req);

// --- Write (3) + ADR-008 read addition (1) ---------------------------------

export const createNote = (req: CreateNoteRequest): Promise<ToolResult<WriteResult>> =>
  callTool('create_note', req);

export const appendToNote = (req: AppendToNoteRequest): Promise<ToolResult<WriteResult>> =>
  callTool('append_to_note', req);

export const updateNote = (req: UpdateNoteRequest): Promise<ToolResult<SaveResult>> =>
  callTool('update_note', req);

/** Editor v0.2 — soft-delete a note. Idempotent server-side. */
export const deleteNote = (req: { id: string }): Promise<ToolResult<WriteResult>> =>
  callTool('delete_note', req);

// The restore counterpart of delete_note also exists as an MCP tool (catalogue.py /
// `restore_note`) but the frontend goes through the REST endpoint `POST /notes/{id}/restore`
// exported from `endpoints.ts` — kept there for consistency with the rest of the
// soft-delete recovery surface (which uses REST throughout). External MCP clients still
// see the tool.

/** Editor v0.2 Phase 7 — toggle the notes.starred boolean. */
export const setNoteStarred = (
  req: { id: string; starred: boolean },
): Promise<ToolResult<WriteResult>> => callTool('set_note_starred', req);

/** Mark an extracted action item open/resolved/dropped. The `status` column both the editor
 *  checkbox and the read tools (`get_action_items` / `get_note`) project from, so this write
 *  is immediately visible to MCP and the in-app chat. */
export const setActionItemStatus = (
  req: { actionItemId: string; status: 'open' | 'resolved' | 'dropped' },
): Promise<ToolResult<WriteResult>> => callTool('set_action_item_status', req);

/** Hand-create a user-owned action item on a note (migration 0017). `dueDate` is YYYY-MM-DD. */
export const createActionItem = (
  req: { noteId: string; text: string; dueDate?: string | null },
): Promise<ToolResult<WriteResult>> => callTool('create_action_item', req);

/** Edit an action item's text and/or due date. `clearDueDate=true` clears the due date;
 *  otherwise `dueDate` (YYYY-MM-DD) is written only when present. */
export const updateActionItem = (
  req: { actionItemId: string; text?: string; dueDate?: string | null; clearDueDate?: boolean },
): Promise<ToolResult<WriteResult>> => callTool('update_action_item', req);

/** Soft-delete one action item (migration 0017). */
export const deleteActionItem = (
  req: { actionItemId: string },
): Promise<ToolResult<WriteResult>> => callTool('delete_action_item', req);

/** Apply a user-authored tag (is_auto=false) to a note. Canonicalized server-side. */
export const addNoteTag = (
  req: { noteId: string; tag: string },
): Promise<ToolResult<WriteResult>> => callTool('add_note_tag', req);

/** Remove a tag from a note (records a suppression so an auto tag isn't re-added). */
export const removeNoteTag = (
  req: { noteId: string; tag: string },
): Promise<ToolResult<WriteResult>> => callTool('remove_note_tag', req);

export const noteTitles = (
  req: NoteTitlesRequest,
): Promise<ToolResult<{ items: NoteTitleItem[] }>> => callTool('note_titles', req);

// --- Editor workspace (1) ---------------------------------------------------

/** ``list_notebooks`` — every live notebook + its live-note count, ordered for tree
 *  assembly. Backs the editor's left-pane notebook tree. */
export const listNotebooks = (
  req: ListNotebooksRequest = {},
): Promise<ToolResult<NotebookList>> => callTool('list_notebooks', req);
